"use client";

import { useAuth } from "@/contexts/AuthContext";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

function getGreeting(hour: number): string {
  if (hour >= 0 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 18) return "Good afternoon";
  return "Good evening";
}

function getFirstName(displayName: string | null | undefined): string {
  if (!displayName) return "";
  return displayName.trim().split(/\s+/)[0];
}

function formatCurrentDate(): string {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());
}

export default function HomePage() {
  const { user } = useAuth();
  const now = new Date();
  const greeting = getGreeting(now.getHours());
  const firstName = getFirstName(user?.displayName);
  const formattedDate = formatCurrentDate();

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {/* Page header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {greeting}{firstName ? `, ${firstName}` : ""}
        </Typography>
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: "24px",
            px: 1,
            py: 1,
            flexShrink: 0,
            ml: 2,
          }}
        >
          <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
            {formattedDate}
          </Typography>
        </Box>
      </Box>

      {/* Content card */}
      <Paper
        elevation={1}
        sx={{
          borderRadius: "24px",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          minHeight: 484,
        }}
      >
        {/*
         * Text content: pl matches Figma's pl-176 at lg.
         * pr reserves space so text doesn't run under the animal collage.
         * No maxWidth on this outer box — let the padding constrain the space.
         */}
        <Box
          sx={{
            flex: 1,
            zIndex: 1,
            position: "relative",
            maxWidth: { xs: 610, lg: 610 },
            margin: "auto",
            padding: "16px"

          }}
        >
          <Typography
            variant="h5"
            component="p"
            sx={{ textAlign: "center", mb: 2 }}
          >
            <Box
              component="span"
              sx={{
                textDecoration: "underline",
                textDecorationColor: "rgba(92,204,194,0.87)",
                textDecorationThickness: "15.5%",
              }}
            >
              Good things
            </Box>
            {" are coming to this space "}
            {/* Home icon inline, anchored immediately after 'space' */}
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                whiteSpace: 'nowrap', // prevents this chunk from breaking
              }}
            ><Box
                component="img"
                src="/images/home/home-icon.svg"
                alt=""
                width={32}
                height={32}
                style={{ verticalAlign: "middle" }}
              /></Box>
            
          </Typography>
          <Typography variant="h5" component="p" sx={{ textAlign: "center" }}>
            {"We're working on something useful for you here. Until then, your "}
            <strong>pet owner data</strong>
            {", "}
            <strong>conversations</strong>
            {",  "}
            <strong>performance</strong>
            {" and "}
            <strong>more</strong>
            {" are all waiting for you in the navigation on the left."}
          </Typography>
        </Box>

        {/* Arrow squiggle — decorative, hidden on small screens */}
        <Box
          component="img"
          src="/images/home/arrow.svg"
          alt=""
          sx={{
            position: "absolute",
            left: 16,
            top: "45%",
            width: 250,
            height: 118,
            transform: "rotate(15deg)",
            display: { xs: "none", lg: "block" },
          }}
        />

        {/* Animal collage — decorative, hidden on small screens */}
        <Box
          sx={{
            position: "absolute",
            right: 16,
            bottom: 0,
            width: 341,
            height: 310,
            display: { xs: "none", lg: "block" },
          }}
        >
          {/* Bunny — top right of collage */}
          <Box
            component="img"
            src="/images/home/bunny.svg"
            alt=""
            sx={{
              position: "absolute",
              left: 180,
              top: 0,
              width: 107,
              height: 120,
              transform: "scaleY(-1) rotate(180deg)",
            }}
          />
          {/* Mouse — middle left of collage */}
          <Box
            component="img"
            src="/images/home/mouse.svg"
            alt=""
            sx={{
              position: "absolute",
              left: 106,
              top: 77,
              width: 62,
              height: 42,
            }}
          />
          {/* Dog — layered above cat */}
          <Box
            component="img"
            src="/images/home/dog.svg"
            alt=""
            sx={{
              position: "absolute",
              left: 65,
              top: 120,
              width: 196,
              height: 94,
              transform: "scaleY(-1) rotate(180deg)",
            }}
          />
          {/* Cat — base of the stack, full width */}
          <Box
            component="img"
            src="/images/home/cat.svg"
            alt=""
            sx={{
              position: "absolute",
              left: 0,
              top: 202,
              width: 341,
              height: 108,
              transform: "scaleY(-1) rotate(180deg)",
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
